/**
 * Language backend for the embed app's i18next instance.
 *
 * Mirrors the pattern in `apps/WunderGuide/lang/languageBackend.ts` but
 * intentionally simpler — the embed runs inside a partner's page where
 * bundle size and request count matter more than the RN client's
 * network-first / cache / bundled three-tier stack.
 *
 * Each entry is a static `() => import('./X.json')` so Vite (like Metro)
 * can split each language into its own chunk. Result: only `en` ships in
 * the initial bundle; non-English locales are fetched on demand the first
 * time the visitor's resolved locale needs them.
 *
 * Adding a new language is two lines:
 *   1. Drop the JSON file in this directory.
 *   2. Add `'<tag>': () => import('./<tag>.json'),` to BUNDLED_LOADERS below.
 */

// Each entry resolves to a specific JSON file at build time (Vite needs
// static `import()` paths). Aliases like `pt` → `pt-BR.json` defend
// against the i18next lookup chain asking for the bare base after the
// resolveLocale chain — without them, a request for 'pt' would fall
// through to the English fallback even though we have a Portuguese
// bundle on disk. Same idea as RN's localeMap.json + languageBackend.
const BUNDLED_LOADERS: Record<string, () => Promise<unknown>> = {
  en: () => import('./en.json'),
  es: () => import('./es.json'),
  fr: () => import('./fr.json'),
  de: () => import('./de.json'),
  it: () => import('./it.json'),
  nl: () => import('./nl.json'),
  ja: () => import('./ja.json'),
  ko: () => import('./ko.json'),
  haw: () => import('./haw.json'),
  // Portuguese — only Brazilian bundle exists today; alias `pt` to it
  // since most Portuguese-language requests hit `pt` (post region-strip).
  pt: () => import('./pt-BR.json'),
  'pt-BR': () => import('./pt-BR.json'),
  // Chinese — full script tags are canonical. Bare `zh` aliases to
  // Simplified (the wider audience). `zh-CN`/`zh-SG` and `zh-TW`/`zh-HK`
  // resolve through resolveLocale.ts before reaching this map.
  zh: () => import('./zh-Hans.json'),
  'zh-Hans': () => import('./zh-Hans.json'),
  'zh-Hant': () => import('./zh-Hant.json'),
};

/** All locales we have a JSON file for. */
export const SUPPORTED_LOCALES: readonly string[] =
  Object.keys(BUNDLED_LOADERS);

/**
 * The async loader passed to `i18next-resources-to-backend`. i18next
 * calls this with the resolved language code (after our resolveLocale
 * has run and after i18next's own load: 'languageOnly' / fallback chain).
 * We respond with the parsed JSON or null if we don't carry that locale —
 * i18next then falls through to its fallbackLng ('en').
 */
export async function languageBackendLoader(
  lng: string,
  _ns: string,
): Promise<Record<string, unknown> | null> {
  const loader = BUNDLED_LOADERS[lng];
  if (!loader) return null;
  try {
    const mod = (await loader()) as
      | Record<string, unknown>
      | { default?: Record<string, unknown> };
    // Vite/TS JSON imports surface as `{ default: {...} }` under
    // esModuleInterop; the bare-object shape is what i18next wants.
    if (mod && typeof mod === 'object' && 'default' in mod && mod.default) {
      return mod.default as Record<string, unknown>;
    }
    return mod as Record<string, unknown>;
  } catch {
    return null;
  }
}
